import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { postLifecycleGroups } from '../api/lifecycle';
import { useLatestWins } from '../utils/latestWins';
import { computeBidHealthByAppPlatform } from '../validation/bidHealth';
import { computeBidderYield } from '../validation/bidderYield';
import { computeInventoryFlow } from '../validation/inventoryFlow';
import { computeBidNoFill, SRC_LABEL } from '../validation/bidNoFill';
import { buildSlotLifecycles } from './AdLifecycleView';
import { buildVideoSessions, VideoBidHealthPanel } from './VideoLifecycleView';

import useCoalescedPost from '../utils/useCoalescedPost';

/**
 * resolveBidHealthSource(server, source, local) — which implementation's
 * answer this panel renders for `inventoryFlow`, mirroring
 * AdLifecycleView.jsx's resolveGraphSource: a server response missing either
 * required field is treated as unusable and the WHOLE local fallback is used
 * instead of patching in individual pieces — a panel whose parts disagree is
 * exactly the bug this migration exists to kill. `local.inv` is a THUNK
 * (WP1c) so `buildSlotLifecycles` — expensive on a real session — only runs
 * when the fallback is actually needed.
 *
 * bidNoFill is deliberately NOT resolved here — see the comment on the
 * `noFill` useMemo below for why the server's field cannot yet be treated as
 * a drop-in replacement for the local computation.
 */
export function resolveBidHealthSource(serverPayload, source, local) {
  const complete = source === 'server'
    && !!serverPayload
    && !!serverPayload.inventoryFlow
    && !!serverPayload.inventoryFlowDetail
    && Array.isArray(serverPayload.inventoryFlowDetail.units)
    && Array.isArray(serverPayload.inventoryFlowDetail.perSlot)
    && Array.isArray(serverPayload.inventoryFlowDetail.leaks)
    // WP3a/WP3b ride the same POST and the same all-or-nothing decision: an
    // older proxy that predates them still answers with inventoryFlow, and
    // taking that while bidHealth/bidderYield came from the browser is exactly
    // the half-populated panel `complete` exists to prevent.
    && Array.isArray(serverPayload.bidHealthByAppPlatform)
    && !!serverPayload.bidderYield;

  if (!complete) {
    return {
      usingServer: false,
      inv: local.inv(),
      bidHealth: local.bidHealth(),
      bidderYield: local.bidderYield(),
    };
  }
  return {
    usingServer: true,
    bidHealth: serverPayload.bidHealthByAppPlatform,
    bidderYield: serverPayload.bidderYield,
    // The server splits the rollup (`inventoryFlow`, always present) from the
    // per-item detail (`inventoryFlowDetail`, present when `detail:true` —
    // postLifecycleGroups always sends it) to avoid repeating per-slot detail
    // in the slim default payload (summarize.js). Reassembled here into the
    // same {units, perSlot, leaks, totals} shape computeInventoryFlow returns
    // locally, so every consumer below reads one shape regardless of source.
    inv: {
      units: serverPayload.inventoryFlowDetail.units,
      perSlot: serverPayload.inventoryFlowDetail.perSlot,
      leaks: serverPayload.inventoryFlowDetail.leaks,
      totals: serverPayload.inventoryFlow,
    },
  };
}

// semantic palette shared by the flow charts/tables (kept off the brand accent)
const C_LEAK = '#9B2C6F', C_LAT = '#B7791F', C_OUTBID = '#C05621', C_NOBID = '#94A3B8',
      C_TEAL = '#2C7A7B', C_GOOD = '#1D9E75', C_WASTE = '#A32D2D';

const SEV_COLOR = { fail: C_WASTE, warn: '#854F0B', info: '#1c5f8a', pass: C_GOOD };
const SEV_BG    = { fail: 'var(--error-bg)', warn: 'var(--warn-bg)', info: 'var(--info-bg)', pass: 'var(--success-bg)' };

/**
 * DemandWastedPanel — "we had a bid and served nothing".
 *
 * Counts the ad requests where Prebid, Amazon APS or SpringServe returned a real
 * bid but the ad server came back with no fill. Distinct from the "Bids missed"
 * donut below, which is about demand that lost an auction it reached; this is
 * demand that never got the chance to lose one. Every row here is a line item,
 * price bucket or targeting bug worth money.
 */
function DemandWastedPanel({ nf }) {
  const [open, setOpen] = useState(false);
  if (!nf.withDemand) return null;

  const C = SEV_COLOR[nf.severity] || C_NOBID;
  const BG = SEV_BG[nf.severity] || 'var(--surface)';
  const srcChips = Object.entries(nf.bySource).filter(([, n]) => n > 0);

  return (
    <div style={{ border: `1px solid ${nf.count ? C + '55' : 'var(--line)'}`, borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.3, color: 'var(--muted)', padding: '6px 12px', background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
        DEMAND WASTED · bid returned → no fill
      </div>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 14, padding: '10px 12px', background: BG }}>
        <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 26, fontWeight: 700, color: C, lineHeight: 1 }}>{nf.count}</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 220 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600 }}>{nf.headline}</span>
          <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>
            {nf.rate}% of requests that had demand · {nf.byChannel.display} display · {nf.byChannel.video} video
            {nf.cpmAtRisk > 0 ? ` · $${nf.cpmAtRisk.toFixed(2)} bid CPM unserved` : ''}
          </span>
        </div>
        <span style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          {srcChips.map(([s, n]) => (
            <span key={s} title={`${SRC_LABEL[s]} bid on ${n} request(s) that came back unfilled`}
                  style={{ fontSize: 11.5, fontWeight: 600, padding: '3px 9px', borderRadius: 12, border: `1px solid ${C}40`, background: 'var(--card)', color: C, whiteSpace: 'nowrap' }}>
              {SRC_LABEL[s]} <b>{n}</b>
            </span>
          ))}
          {nf.notPassedToAdServer > 0 && (
            <span title="The bid never reached the ad server at all — a request-build bug, not an auction outcome."
                  style={{ fontSize: 11.5, fontWeight: 700, padding: '3px 9px', borderRadius: 12, background: C_WASTE, color: '#fff', whiteSpace: 'nowrap' }}>
              {nf.notPassedToAdServer} never reached ad server
            </span>
          )}
          {nf.count > 0 && (
            <button onClick={() => setOpen((v) => !v)}
                    style={{ font: 'inherit', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--line)', background: 'var(--card)', borderRadius: 6, padding: '3px 9px', color: 'var(--muted)' }}>
              {open ? 'Hide' : 'Show'} requests
            </button>
          )}
        </span>
      </div>

      {open && nf.count > 0 && (
        <div style={{ maxHeight: 320, overflowY: 'auto', borderTop: '1px solid var(--line)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--surface)', color: 'var(--muted)', fontSize: 11, textAlign: 'left' }}>
                <th style={{ padding: '5px 10px', fontWeight: 600 }}>Ad unit / session</th>
                <th style={{ padding: '5px 10px', fontWeight: 600 }}>Ch</th>
                <th style={{ padding: '5px 10px', fontWeight: 600 }}>Demand</th>
                <th style={{ padding: '5px 10px', fontWeight: 600, textAlign: 'right' }}>Bid CPM</th>
                <th style={{ padding: '5px 10px', fontWeight: 600 }}>Reached ad server</th>
                <th style={{ padding: '5px 10px', fontWeight: 600 }}>No-fill signal</th>
              </tr>
            </thead>
            <tbody>
              {nf.items.map((it, i) => (
                <tr key={`${it.key}-${i}`} title={it.detail} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={{ padding: '5px 10px', fontWeight: 600, wordBreak: 'break-all' }}>{it.adUnit}</td>
                  <td style={{ padding: '5px 10px', color: 'var(--muted)' }}>{it.channel === 'video' ? 'video' : 'display'}</td>
                  <td style={{ padding: '5px 10px' }}>
                    {it.sources.map((s) => SRC_LABEL[s]).join(' + ')}
                    {it.topBidder ? <span style={{ color: 'var(--muted)' }}> ({it.topBidder})</span> : null}
                  </td>
                  <td style={{ padding: '5px 10px', textAlign: 'right', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                    {it.bidCpm != null ? `$${it.bidCpm.toFixed(2)}` : <span style={{ color: '#b0bac6' }}>—</span>}
                  </td>
                  <td style={{ padding: '5px 10px', color: it.passedToGAM === false ? C_WASTE : 'var(--muted)', fontWeight: it.passedToGAM === false ? 700 : 400 }}>
                    {it.passedToGAM === false ? 'no' : it.passedToGAM === true ? 'yes' : '—'}
                  </td>
                  <td style={{ padding: '5px 10px', color: 'var(--muted)' }}>{it.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * BidsMissedDonut — composition of header-bidding demand that returned but never
 * rendered: leaked (never sent to GAM), render-fail (won price, no render), outbid
 * by AdX, and no-bid. Segment/legend click filters the inventory table via onPick.
 */
function BidsMissedDonut({ inv, onPick }) {
  const cats = useMemo(() => {
    const per = inv.perSlot || [];
    const sum = (rows) => rows.reduce((a, r) => a + (r.hbCpm || 0), 0);
    const latency = per.filter((r) => r.anomaly === 'latency');
    const outbid = per.filter((r) => r.anomaly === 'outbid');
    const nobid = per.filter((r) => r.anomaly === 'nobid');
    // No fill despite a bid on the table — demand that never even reached an auction.
    const wasted = per.filter((r) => r.anomaly === 'nofill' && r.wastedDemand);
    // Slow (>30s) renders that DID paint — folded into the render-problem slice so
    // revenue-at-risk isn't undercounted. Excludes slots already in `latency`
    // (source render-fail) to avoid double-counting the same slot.
    const slow = per.filter((r) => r.slowRenderMs != null && r.anomaly !== 'latency');
    return [
      { id: 'leak',    label: 'Leaked · never sent to GAM',          color: C_LEAK,   count: inv.leaks.length,          dollars: inv.leaks.reduce((a, l) => a + (l.cpm || 0), 0) },
      { id: 'latency', label: 'Won price · didn’t render / >30s late', color: C_LAT,  count: latency.length + slow.length, dollars: sum(latency) + sum(slow) },
      { id: 'outbid',  label: 'Outbid by AdX',                       color: C_OUTBID, count: outbid.length,             dollars: sum(outbid) },
      { id: 'wasted',  label: 'Bid returned · no fill',              color: C_WASTE,  count: wasted.length,             dollars: sum(wasted) },
      { id: 'nobid',   label: 'No bid returned',                     color: C_NOBID,  count: nobid.length,              dollars: 0 },
    ];
  }, [inv]);

  const total = cats.reduce((a, c) => a + c.count, 0);
  const totalDol = cats.reduce((a, c) => a + c.dollars, 0);
  if (!total) return null;

  const R = 62, r = 40, cx = 72, cy = 72, Circ = 2 * Math.PI * ((R + r) / 2), sw = R - r;
  let off = 0;
  const segs = cats.filter((c) => c.count).map((c) => {
    const len = (c.count / total) * Circ;
    const seg = (
      <circle key={c.id} r={(R + r) / 2} cx={cx} cy={cy} fill="none" stroke={c.color} strokeWidth={sw}
              strokeDasharray={`${len} ${Circ - len}`} strokeDashoffset={-off}
              transform={`rotate(-90 ${cx} ${cy})`} style={{ cursor: 'pointer' }}
              onClick={() => onPick && onPick(c.id)}><title>{`${c.label}: ${c.count}`}</title></circle>
    );
    off += len; return seg;
  });

  return (
    <div style={{ padding: '14px 4px 4px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }}>
        Bids missed <span style={{ fontWeight: 600, textTransform: 'none', color: '#b0bac6' }}>— HB demand that returned but didn’t render · {total} slots · ${totalDol.toFixed(2)} beaten / lost</span>
      </div>
      <div style={{ display: 'flex', gap: 22, alignItems: 'center', flexWrap: 'wrap' }}>
        <svg width="144" height="144" viewBox="0 0 144 144" role="img" aria-label="Missed-bids composition">
          {segs}
          <text x="72" y="68" textAnchor="middle" fontFamily="ui-monospace, Menlo, monospace" fontSize="26" fontWeight="600" fill="var(--ink)">{total}</text>
          <text x="72" y="86" textAnchor="middle" fontSize="10" fill="#9AA6B4">missed</text>
        </svg>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 240, flex: 1 }}>
          {cats.map((c) => (
            <div key={c.id} onClick={() => c.count && onPick && onPick(c.id)}
                 style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, padding: '4px 7px', borderRadius: 7, cursor: c.count ? 'pointer' : 'default', opacity: c.count ? 1 : 0.5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: c.color, flex: 'none' }} />
              <span style={{ fontWeight: 600 }}>{c.label}</span>
              <span style={{ marginLeft: 'auto', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11.5, color: C_OUTBID, width: 64, textAlign: 'right' }}>{c.dollars ? `$${c.dollars.toFixed(2)}` : ''}</span>
              <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 600, fontVariantNumeric: 'tabular-nums', width: 28, textAlign: 'right' }}>{c.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * BidderBubble — bid rate × avg CPM scatter; bubble size = # bids, color = win rate.
 * The AssertiveYield-style performance landscape.
 */
function BidderBubble({ yield: y }) {
  const [tip, setTip] = useState(null);
  const bidders = y.bidders.filter((b) => b.avgCpm != null);
  if (!bidders.length) return null;
  const W = 620, H = 214, padL = 46, padR = 18, padT = 16, padB = 32;
  const xMax = 100, yMax = Math.max(1, ...bidders.map((b) => b.avgCpm)) * 1.15;
  const bx = (v) => padL + (v / xMax) * (W - padL - padR);
  const by = (v) => H - padB - (v / yMax) * (H - padT - padB);
  const rOf = (b) => 4 + Math.sqrt(b.bids) * 3.0;
  const winCol = (w) => (w >= 25 ? C_GOOD : w >= 10 ? C_TEAL : w > 0 ? C_LAT : C_NOBID);

  return (
    <div style={{ padding: '14px 4px 4px', position: 'relative' }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }}>
        Bidder performance <span style={{ fontWeight: 600, textTransform: 'none', color: '#b0bac6' }}>— bid rate × avg CPM · bubble = # bids · color = win rate</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Bidder performance scatter">
          {[0, yMax / 2, yMax].map((t, i) => (
            <g key={`y${i}`}><line x1={padL} y1={by(t)} x2={W - padR} y2={by(t)} stroke="var(--line)" />
              <text x={padL - 7} y={by(t) + 3} textAnchor="end" fontSize="10" fill="#9AA6B4" fontWeight="600">${t.toFixed(1)}</text></g>
          ))}
          {[0, 25, 50, 75, 100].map((t) => (
            <g key={`x${t}`}><line x1={bx(t)} y1={padT} x2={bx(t)} y2={H - padB} stroke="var(--line)" />
              <text x={bx(t)} y={H - padB + 15} textAnchor="middle" fontSize="10" fill="#9AA6B4" fontWeight="600">{t}%</text></g>
          ))}
          <text x={W / 2} y={H - 3} textAnchor="middle" fontSize="10" fill="#9AA6B4" fontWeight="600">Bid rate →</text>
          {[...bidders].sort((a, b) => b.bids - a.bids).map((b) => {
            const cx = bx(b.bidRate), cy = by(b.avgCpm), rr = rOf(b);
            return (
              <g key={b.seat} style={{ cursor: 'pointer' }}
                 onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY, b })} onMouseLeave={() => setTip(null)}>
                <circle cx={cx} cy={cy} r={rr} fill={winCol(b.winRate)} fillOpacity="0.68" stroke={winCol(b.winRate)} strokeWidth="1" />
                <text x={cx} y={cy - rr - 4} textAnchor="middle" fontSize="10.5" fontWeight="600" fill="#334155">{b.seat}</text>
              </g>
            );
          })}
        </svg>
      </div>
      {tip && (
        <div style={{ position: 'fixed', left: Math.min(window.innerWidth - 232, tip.x + 14), top: tip.y + 14, pointerEvents: 'none',
                      background: '#1C2530', color: '#fff', fontSize: 11.5, padding: '7px 10px', borderRadius: 8, zIndex: 30, boxShadow: '0 6px 20px rgba(0,0,0,.25)' }}>
          <b>{tip.b.seat}</b><br />
          <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontVariantNumeric: 'tabular-nums' }}>
            bid {tip.b.bidRate}% · win {tip.b.winRate}% · ${tip.b.avgCpm.toFixed(2)} · {tip.b.avgMs ?? '—'}ms · {tip.b.bids}/{tip.b.called}
          </span>
        </div>
      )}
    </div>
  );
}

const ANOM_META = {
  leak:    { label: 'Leaked', color: C_LEAK },
  latency: { label: 'No/late render', color: C_LAT },
  outbid:  { label: 'AdX outbid', color: C_OUTBID },
  rendered:{ label: 'HB rendered', color: C_GOOD },
  nobid:   { label: 'No bid', color: C_NOBID },
  nofill:  { label: 'No fill', color: C_NOBID },
  wasted:  { label: 'Bid · no fill', color: C_WASTE },
};

/** InventoryTable — per ad unit flow rollup with click-to-expand per-slot rows. */
function InventoryTable({ inv, filter }) {
  const [open, setOpen] = useState({});
  const slotsByIu = useMemo(() => {
    const m = new Map();
    for (const s of inv.perSlot || []) { if (!m.has(s.iu)) m.set(s.iu, []); m.get(s.iu).push(s); }
    return m;
  }, [inv]);

  const units = useMemo(() => {
    if (!filter) return inv.units;
    return inv.units.filter((u) => (slotsByIu.get(u.iu) || []).some((s) => filter(s)) || (filter.leakOnly && u.leak));
  }, [inv, filter, slotsByIu]);

  if (!units.length) return <div style={{ padding: 20, textAlign: 'center', color: '#9AA6B4', fontSize: 13 }}>No inventory matches this filter.</div>;
  const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);
  const health = (u) => (u.leak || u.renderFail ? '#A32D2D' : u.rendered ? C_GOOD : C_LAT);
  const short = (iu) => { const p = iu.split('/').filter(Boolean); return p.length > 2 ? '…/' + p.slice(-2).join('/') : iu; };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
        <thead>
          <tr style={{ background: 'var(--surface)', color: '#9AA6B4', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            <th style={{ textAlign: 'left', padding: '6px 10px' }}>Ad unit</th>
            <th style={{ textAlign: 'right', padding: '6px 10px' }}>Reqs</th>
            <th style={{ textAlign: 'right', padding: '6px 10px' }}>PB back</th>
            <th style={{ textAlign: 'right', padding: '6px 10px' }}>APS back</th>
            <th style={{ textAlign: 'right', padding: '6px 10px' }}>→ GAM</th>
            <th style={{ textAlign: 'right', padding: '6px 10px' }}>AdX outbid</th>
            <th style={{ textAlign: 'right', padding: '6px 10px' }}>Rendered</th>
            <th style={{ textAlign: 'right', padding: '6px 10px' }} title="Highest winning HB bid CPM across this unit's slots">HB $</th>
            <th style={{ textAlign: 'right', padding: '6px 10px' }} title="Highest GAM winning CPM (AdX / line-item) across this unit's slots">GAM $</th>
          </tr>
        </thead>
        <tbody>
          {units.map((u) => {
            const slots = slotsByIu.get(u.iu) || [];
            const isOpen = !!open[u.iu];
            return (
              <React.Fragment key={u.iu}>
                <tr onClick={() => setOpen((o) => ({ ...o, [u.iu]: !o[u.iu] }))} style={{ borderTop: '1px solid var(--line)', cursor: 'pointer', background: isOpen ? 'var(--row-active)' : 'transparent' }}>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                    <span style={{ display: 'inline-block', width: 11, color: '#9AA6B4', transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▶</span>
                    <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: health(u), margin: '0 7px' }} />
                    <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 600 }} title={u.iu}>{short(u.iu)}</span>
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{u.reqs}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{pct(u.pbBack, u.reqs)}% <span style={{ color: 'var(--muted)', fontSize: 11 }}>{u.pbBack}/{u.reqs}</span></td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{pct(u.apsBack, u.reqs)}% <span style={{ color: 'var(--muted)', fontSize: 11 }}>{u.apsBack}/{u.reqs}</span></td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: u.leak ? C_LEAK : 'inherit' }}>{u.passed}{u.leak ? ` (−${u.leak})` : ''}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: u.outbid ? C_OUTBID : '#b0bac6' }}>{u.outbid || 0}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: u.rendered ? C_GOOD : '#b0bac6' }}>{u.rendered || 0}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'ui-monospace, Menlo, monospace', color: u.hbBids ? '#2c3542' : '#b0bac6' }} title={u.hbBids ? `${u.hbBids} HB bid(s), peak $${u.hbCpmMax.toFixed(2)} CPM` : 'no HB bids'}>{u.hbBids ? `$${u.hbCpmMax.toFixed(2)}` : '—'}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'ui-monospace, Menlo, monospace', color: u.gamWins ? '#2c3542' : '#b0bac6' }} title={u.gamWins ? `${u.gamWins} GAM win(s), peak $${u.gamCpmMax.toFixed(2)} CPM` : 'no GAM winning CPM captured'}>{u.gamWins ? `$${u.gamCpmMax.toFixed(2)}` : '—'}</td>
                </tr>
                {isOpen && slots.map((s, i) => {
                  // A no-fill that had a bid on the table gets its own (louder) badge.
                  const m = ANOM_META[s.wastedDemand && s.anomaly === 'nofill' ? 'wasted' : s.anomaly] || ANOM_META.nobid;
                  const delta = s.anomaly === 'outbid' && s.winnerCpm != null && s.hbCpm != null ? (s.winnerCpm - s.hbCpm) : null;
                  return (
                    <tr key={`${u.iu}-${i}`} style={{ background: 'var(--surface)', fontSize: 11.5 }}>
                      <td style={{ padding: '5px 10px 5px 34px' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', padding: '2px 6px', borderRadius: 5, color: m.color, background: `${m.color}1a` }}>{m.label}</span>
                      </td>
                      <td colSpan={7} style={{ padding: '5px 10px', color: '#5A6675', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                        {s.hbCpm != null ? `HB $${s.hbCpm.toFixed(2)}` : 'no HB bid'}
                        {s.winnerCpm != null ? ` · AdX $${s.winnerCpm.toFixed(2)}` : ''}
                        {delta != null ? ` · AdX +$${delta.toFixed(2)}` : ''}
                        {` · rendered by ${s.rendered}`}
                      </td>
                      <td />
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * BidderYieldTable — per-bidder scorecard (AssertiveYield-style) over the proxy's
 * already-computed per-seat table (validData.tables.bids). Bid rate / win rate /
 * avg CPM / response ms / timeout, sortable. S2S bidders only (see bidderYield.js).
 */
const YIELD_COLS = [
  { key: 'seat',        label: 'Bidder',    align: 'left'  },
  { key: 'bidRate',     label: 'Bid rate',  align: 'right' },
  { key: 'winRate',     label: 'Win rate',  align: 'right' },
  { key: 'avgCpm',      label: 'Avg CPM',   align: 'right' },
  { key: 'avgMs',       label: 'Resp ms',   align: 'right' },
  { key: 'timeoutRate', label: 'Timeout',   align: 'right' },
  { key: 'bids',        label: 'Bids',      align: 'right' },
];

function BidderYieldTable({ yield: y }) {
  const [sort, setSort] = useState({ key: 'bidRate', dir: -1 });
  const bidders = useMemo(() => {
    const arr = [...y.bidders];
    const { key, dir } = sort;
    arr.sort((a, b) => {
      const av = a[key], bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;               // nulls last regardless of dir
      if (bv == null) return -1;
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
    return arr;
  }, [y.bidders, sort]);

  if (!bidders.length) return null;
  const th = (c) => {
    const active = sort.key === c.key;
    return (
      <th key={c.key}
          onClick={() => setSort((s) => ({ key: c.key, dir: s.key === c.key ? -s.dir : (c.key === 'avgMs' || c.key === 'timeoutRate' ? 1 : -1) }))}
          style={{ textAlign: c.align, padding: '6px 10px', cursor: 'pointer', whiteSpace: 'nowrap',
                   color: active ? 'var(--fg, #111)' : 'var(--muted)', fontWeight: active ? 700 : 600, userSelect: 'none' }}>
        {c.label}{active ? (sort.dir < 0 ? ' ↓' : ' ↑') : ''}
      </th>
    );
  };
  const health = (r) => (r.bidRate >= 35 ? '#1D9E75' : r.bidRate >= 18 ? '#854F0B' : '#A32D2D');

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden', marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 600, letterSpacing: 0.3,
                    color: 'var(--muted)', padding: '6px 12px', background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
        <span>BID HEALTH · bidder yield · {y.totalBidders} S2S bidders · {y.auctionsWithTable} auctions</span>
        <span style={{ marginLeft: 'auto', fontWeight: 500, opacity: 0.75 }} title="Bid rate = bid responses ÷ auctions the bidder was called in (from ext.responsetimemillis). Server-to-server bidders only.">
          bid rate = responses ÷ called
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
          <thead>
            <tr style={{ background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>{YIELD_COLS.map(th)}</tr>
          </thead>
          <tbody>
            {bidders.map((r) => (
              <tr key={r.seat} style={{ borderTop: '1px solid var(--line)' }}>
                <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>
                  <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: health(r), marginRight: 7 }} />
                  <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 600 }}>{r.seat}</span>
                </td>
                <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                  <div style={{ fontWeight: 600 }}>{r.bidRate}% <span style={{ color: 'var(--muted)', fontWeight: 500, fontSize: 11 }}>{r.bids}/{r.called}</span></div>
                  <div style={{ height: 4, borderRadius: 3, background: 'var(--line)', marginTop: 3, overflow: 'hidden' }}>
                    <span style={{ display: 'block', height: '100%', width: `${r.bidRate}%`, background: health(r) }} />
                  </div>
                </td>
                <td style={{ padding: '7px 10px', textAlign: 'right', color: r.winRate ? '#1D9E75' : 'var(--muted)', fontWeight: 600 }}>
                  {r.winRate}% <span style={{ color: 'var(--muted)', fontWeight: 500, fontSize: 11 }}>{r.wins}/{r.bids}</span>
                </td>
                <td style={{ padding: '7px 10px', textAlign: 'right' }}>{r.avgCpm != null ? `$${r.avgCpm.toFixed(2)}` : <span style={{ color: '#b0bac6' }}>—</span>}</td>
                <td style={{ padding: '7px 10px', textAlign: 'right', color: r.avgMs != null && r.avgMs >= 400 ? '#854F0B' : 'inherit' }}>{r.avgMs != null ? r.avgMs : <span style={{ color: '#b0bac6' }}>—</span>}</td>
                <td style={{ padding: '7px 10px', textAlign: 'right', color: r.timeoutRate >= 20 ? '#854F0B' : 'var(--muted)' }}>{r.timeoutRate}%</td>
                <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600 }}>{r.bids}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * BidHealthView — header-bidding win rate by app · platform.
 * Moved out of the Lifecycle tab into its own tab so the header-bidding
 * scorecard has room to breathe and Lifecycle stays focused on slot chains.
 *
 * Total vs won per demand source, so a tester sees at a glance whether
 * Prebid/APS are winning or Google AdX is sweeping everything.
 *
 * Also hosts the VIDEO bid-health scorecard (fill / SpringServe+APS bid rate /
 * SS render share), moved here out of the Video tab so all bid-health views
 * live together.
 */
export default function BidHealthView({ rows = [] }) {
  // bidHealth (WP3a) and bidderYield (WP3b) now come from the server through
  // resolveBidHealthSource below; the local calls survive only as its fallback
  // thunks. videoSessions stays local — it is assembled by POST /proxy/video on
  // a separate route that this panel does not call, and bidNoFill needs it (see
  // the note on `noFill`).
  const videoSessions = useMemo(() => buildVideoSessions(rows), [rows]);

  // buildSlotLifecycles is expensive on a real session, and bidNoFill needs
  // it locally regardless of server availability (see below) while
  // inventoryFlow only needs it on the fallback path — so it is built once,
  // lazily, and cached per `rows` rather than eagerly in a useMemo (WP1c).
  const localLifecyclesRef = useRef({ key: null, value: null });
  const buildLocalLifecycles = useCallback(() => {
    const cache = localLifecyclesRef.current;
    if (cache.key === rows) return cache.value;
    const value = buildSlotLifecycles(rows);
    localLifecyclesRef.current = { key: rows, value };
    return value;
  }, [rows]);

  /* ── inventoryFlow: server-authoritative, local fallback ────────────────
   * WP1b (frontend-validation-port-spec.md): the proxy computes the same
   * rollup for GET/POST /proxy/lifecycle (proxy-server/lifecycle/
   * inventoryFlow.js, diff-verified logic-identical to this file's local
   * copy). Posting `{ grouped: false, full: false }` gets just the
   * bidNoFill/inventoryFlow rollups this panel needs, not the groups/
   * orphanReport/redundancies AdLifecycleView also unlocks.
   *
   * Fallback kept for the same reason AdLifecycleView keeps one: this tab
   * works with no proxy at all (imported HAR review, offline triage), and
   * `bidHealthSource` records which implementation produced what's on
   * screen — a fallback that doesn't announce itself is how two answers
   * creep back in.
   */
  const [serverBidHealth, setServerBidHealth] = useState(null);
  const [bidHealthSource, setBidHealthSource] = useState('pending'); // 'pending' | 'server' | 'local' | 'empty'

  const bidHealthLatest = useLatestWins(); // see utils/latestWins.js
  useEffect(() => {
    if (!rows.length) { bidHealthLatest.invalidate(); setServerBidHealth(null); setBidHealthSource('empty'); }
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps
  // Every `rows` change (filters, live traffic) would post the whole session:
  // coalesced to one POST in flight, at most every few seconds.
  useCoalescedPost(() => {
      const seq = bidHealthLatest.begin();
      return postLifecycleGroups(rows, { grouped: false, full: false })
        .then((res) => {
          if (!bidHealthLatest.accept(seq)) return;
          setServerBidHealth(res);
          setBidHealthSource('server');
        })
        .catch(() => {
          if (!bidHealthLatest.accept(seq)) return;
          // Proxy unreachable or errored — fall back to the local computation
          // rather than showing nothing, and say so (provenance badge below).
          setServerBidHealth(null);
          setBidHealthSource('local');
        });
  }, [rows], { enabled: rows.length > 0 });

  const { usingServer: invFromServer, inv, bidHealth, bidderYield } = useMemo(
    () => resolveBidHealthSource(serverBidHealth, bidHealthSource, {
      // Thunks, not values: on the server path none of these run, so the
      // browser never pays to assemble a graph it is about to discard (WP1c).
      inv: () => computeInventoryFlow(buildLocalLifecycles(), rows),
      bidHealth: () => computeBidHealthByAppPlatform(rows),
      bidderYield: () => computeBidderYield(rows),
    }),
    [serverBidHealth, bidHealthSource, buildLocalLifecycles, rows]
  );

  /* ── bidNoFill: LOCAL ONLY — not yet safe to source from the server ──────
   * WP1b investigated wiring this to serverPayload.bidNoFill and stopped
   * short. proxy-server/lifecycle/summarize.js calls
   * `computeBidNoFill(lifecycles, [])` — an explicit empty array — because
   * the server's bidNoFill.js has no way to see this session's video
   * sessions (buildVideoSessions() has never run inside summarize.js's
   * request; POST /proxy/video assembles them on a wholly separate route and
   * that call is not made here). proxy-server/lifecycle/bidNoFill.js's own
   * header says as much: "Server-side callers of this module can only pass
   * [] for videoSessions... the video half of this detector is therefore
   * untested against real proxy data."
   *
   * Concretely: serverPayload.bidNoFill.byChannel.video is always 0 and
   * serverPayload.bidNoFill.items never contains a video entry, regardless
   * of what the session actually did — a DAI stream or SpringServe VAST
   * no-fill would silently vanish from the DEMAND WASTED panel above,
   * exactly the highest-value count this detector exists to surface. That
   * is a silent undercount, not a "the proxy answered, use it" case, so this
   * stays local until summarize.js is wired to a server-side video-session
   * source (tracked with the WP3d video port). Flagged in the WP1b report
   * rather than shipped quietly.
   */
  const noFill = useMemo(
    () => computeBidNoFill(buildLocalLifecycles(), videoSessions),
    [buildLocalLifecycles, videoSessions]
  );

  const [flowView, setFlowView] = useState('inventory');
  const [invFilter, setInvFilter] = useState(null);

  const pickAnom = (id) => setInvFilter(() => {
    if (id === 'leak') { const f = () => false; f.leakOnly = true; f._id = id; return f; }
    // 'wasted' is a subset of the nofill bucket, not an anomaly value of its own.
    if (id === 'wasted') { const f = (s) => s.anomaly === 'nofill' && s.wastedDemand; f._id = id; return f; }
    const f = (s) => s.anomaly === id; f._id = id; return f;
  });

  const hasFlow = inv.units.length > 0 || bidderYield.bidders.length > 0;
  if (!bidHealth.length && !videoSessions.length && !bidderYield.bidders.length && !inv.units.length) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
        No header-bidding activity captured yet — once GAM requests with Prebid / APS
        signals come through, win-rate by app &amp; platform shows up here.
      </div>
    );
  }

  return (
    <div style={{ padding: 8 }}>
      {/* Demand that bid but was never served — display + video in one count */}
      <DemandWastedPanel nf={noFill} />

      {/* Video bid health (moved from the Video tab) */}
      <VideoBidHealthPanel sessions={videoSessions} />

      {bidHealth.length > 0 && (
      <div style={{ border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.3, color: 'var(--muted)', padding: '6px 12px', background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
          BID HEALTH · header-bidding win rate by app · platform
        </div>
        {bidHealth.map((s) => {
          const C  = { fail: '#A32D2D', warn: '#854F0B', pass: '#1D9E75', info: '#6b7280' }[s.severity] || '#6b7280';
          const BG = { fail: 'var(--error-bg)', warn: 'var(--warn-bg)', pass: 'var(--success-bg)', info: 'var(--surface)' }[s.severity] || 'var(--surface)';
          return (
            <div key={s.key} title={s.headline + (s.flags.length ? '\n\n⚑ ' + s.flags.join('\n⚑ ') : '')}
                 style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 6, columnGap: 12, padding: '8px 12px', fontSize: 12, background: BG, borderTop: '1px solid var(--line)' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: C, flexShrink: 0 }} />
              <span style={{ fontWeight: 600, minWidth: 104, whiteSpace: 'nowrap' }}>{s.key}</span>
              <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                {s.gamRequests} reqs · {s.filled} filled{s.noFill ? ` · ${s.noFill} no-fill` : ''}
              </span>
              <span style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', columnGap: 12, rowGap: 4, alignItems: 'center' }}>
                <span style={{ whiteSpace: 'nowrap' }} title="Prebid creatives that actually RENDERED / filled slots">PB <b style={{ color: C }}>{s.winMix.prebid}</b>/{s.filled}</span>
                <span style={{ whiteSpace: 'nowrap' }} title="Amazon APS creatives that actually RENDERED / filled slots">APS <b style={{ color: C }}>{s.winMix.aps}</b>/{s.filled}</span>
                <span style={{ whiteSpace: 'nowrap' }} title="Google AdX creatives rendered / filled slots">AdX <b>{s.winMix.adx}</b>/{s.filled}</span>
                {s.winMix.direct > 0 && <span style={{ whiteSpace: 'nowrap' }} title="Direct / House creatives rendered / filled slots">Direct <b>{s.winMix.direct}</b>/{s.filled}</span>}
                <span
                  title={`Of the ${s.hbPassed} slot(s) where an HB bid was PASSED to GAM, an HB creative actually rendered ${s.hbRendered}×. ${s.hbPassedLost}× the bid was passed but AdX/direct rendered instead (HB lost the GAM auction). Bid ≠ render.`}
                  style={{ color: s.hbRenderRatePct >= 50 ? C : '#854F0B', fontWeight: 700, whiteSpace: 'nowrap' }}>
                  HB rendered {s.hbRenderRatePct}% <span style={{ opacity: 0.6, fontWeight: 600 }}>({s.hbRendered}/{s.hbPassed})</span>
                </span>
              </span>
            </div>
          );
        })}
      </div>
      )}

      {/* ── Bid-flow explorer: Inventory ↔ Bidders ─────────────────────── */}
      {hasFlow && (
        <div style={{ border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden', marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
            <div style={{ display: 'inline-flex', background: 'var(--surface)', borderRadius: 8, padding: 3, gap: 3 }}>
              {['inventory', 'bidder'].map((v) => (
                <button key={v} onClick={() => { setFlowView(v); setInvFilter(null); }}
                        style={{ font: 'inherit', fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer', padding: '5px 13px', borderRadius: 6,
                                 background: flowView === v ? 'var(--card)' : 'transparent', color: flowView === v ? 'var(--primary)' : 'var(--muted)',
                                 boxShadow: flowView === v ? '0 1px 2px rgba(16,24,40,.08)' : 'none' }}>
                  {v === 'inventory' ? 'By inventory' : 'By bidder'}
                </button>
              ))}
            </div>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>
              {flowView === 'inventory'
                ? <>{inv.totals.units} ad units · PB back {inv.totals.pbBackRate}% · {inv.totals.leaks} leaked · {inv.totals.renderFail} render-fail · {inv.totals.wastedDemand} bid·no-fill</>
                : <>{bidderYield.totalBidders} S2S bidders · {bidderYield.auctionsWithTable} auctions</>}
            </span>
            {/* Provenance — shown ONLY when the panel fell back to local
                computation, because "the server answered" is the expected case
                and a badge for it is noise; a local fallback is the exception
                a reader needs to know about. Same wording/styling as
                AdLifecycleView.jsx's graphSource badge (WP1b).
                NOT gated on flowView: the resolver decides inventory flow,
                bid health AND bidder yield together, so a badge that appeared
                only on the inventory sub-view would leave the bidder view
                silently local — the unannounced fallback this migration exists
                to eliminate. */}
            {!invFromServer && bidHealthSource === 'local' && (
              <span
                title="The proxy could not be reached, so bid health, bidder yield and inventory flow were computed in the browser. They may differ from a server-side sign-off report."
                style={{ fontSize: 11, color: '#854F0B', border: '1px solid #E5C07B', borderRadius: 4, padding: '1px 6px' }}
              >
                computed locally — proxy unreachable
              </span>
            )}
            {flowView === 'inventory' && invFilter && (
              <button onClick={() => setInvFilter(null)}
                      style={{ marginLeft: 'auto', font: 'inherit', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--line)', background: 'var(--card)', borderRadius: 6, padding: '3px 9px', color: 'var(--muted)' }}>
                Clear filter ✕
              </button>
            )}
          </div>

          {flowView === 'inventory' ? (
            <div style={{ padding: '4px 12px 8px' }}>
              <BidsMissedDonut inv={inv} onPick={pickAnom} />
              <InventoryTable inv={inv} filter={invFilter} />
            </div>
          ) : (
            <div style={{ padding: '4px 12px 8px' }}>
              <BidderBubble yield={bidderYield} />
              <BidderYieldTable yield={bidderYield} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
